import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { useMedia, resolveImage } from "../../lib/media";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { ArrowCounterClockwise, FloppyDisk, UploadSimple } from "@phosphor-icons/react";

function SlotRow({ slot, onSaved }) {
  const [rawUrl, setRawUrl] = useState(slot.raw || slot.url);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setRawUrl(slot.raw || slot.url); }, [slot.raw, slot.url]);

  const preview = resolveImage(rawUrl);

  const save = async (urlOverride) => {
    const urlToSave = (urlOverride ?? rawUrl).trim();
    if (!urlToSave) { toast.error("URL cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put(`/media/${slot.key}`, { url: urlToSave });
      toast.success("Image updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the original image?")) return;
    setSaving(true);
    try {
      await api.delete(`/media/${slot.key}`);
      toast.success("Reset to default");
      onSaved();
    } catch {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // data.url is "/api/uploads/{id}" — save relative so it's portable
      setRawUrl(data.url);
      await save(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-ivory border border-border/50 rounded-2xl p-5 flex flex-col md:flex-row gap-5 items-start" data-testid={`media-slot-${slot.key}`}>
      <div className="w-32 h-32 rounded-xl overflow-hidden bg-cream border border-border/40 shrink-0 flex items-center justify-center">
        {preview
          ? <img src={preview} alt={slot.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
          : <span className="text-xs text-muted-foreground">No image</span>
        }
      </div>
      <div className="flex-1 min-w-0 w-full">
        <div className="overline">{slot.key}</div>
        <h3 className="font-serif-display text-xl mt-1">{slot.label}</h3>

        <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground">Image URL or upload</label>
            <Input
              className="bg-cream h-11 mt-1"
              value={rawUrl}
              onChange={(e) => setRawUrl(e.target.value)}
              placeholder="Paste a URL or click Upload"
              data-testid={`media-input-${slot.key}`}
            />
          </div>
          <input
            type="file"
            accept="image/*"
            ref={fileRef}
            onChange={onFilePick}
            className="hidden"
            data-testid={`media-file-${slot.key}`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`media-upload-${slot.key}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-5 h-11 text-sm text-forest hover:bg-forest hover:text-cream transition-all disabled:opacity-50 whitespace-nowrap"
          >
            <UploadSimple size={16}/> {uploading ? "Uploading…" : "Upload from computer"}
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            onClick={() => save()}
            disabled={saving || uploading || rawUrl === (slot.raw || slot.url)}
            data-testid={`media-save-${slot.key}`}
            className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2 text-sm hover:bg-terracotta transition-all disabled:opacity-50"
          >
            <FloppyDisk size={16}/> {saving ? "Saving…" : "Save URL"}
          </button>
          <button
            onClick={reset}
            disabled={saving || uploading}
            data-testid={`media-reset-${slot.key}`}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm text-muted-foreground hover:text-forest hover:border-forest transition-all"
          >
            <ArrowCounterClockwise size={16}/> Reset
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMedia() {
  const { refresh } = useMedia();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/media");
      // keep raw value (so internal /api/uploads/* paths round-trip), but also expose the preview URL
      setSlots((data.slots || []).map(s => ({ ...s, raw: s.url })));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSaved = async () => { await load(); refresh(); };

  return (
    <div data-testid="admin-media">
      <h1 className="font-serif-display text-4xl">Site Content</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Swap any image on the website without a redeploy. Upload from your computer, or paste a URL — the change goes live instantly.
      </p>
      <div className="mt-8 grid gap-4">
        {loading && <div className="text-muted-foreground">Loading…</div>}
        {slots.map(s => <SlotRow key={s.key} slot={s} onSaved={onSaved}/>)}
      </div>
      <div className="mt-10 bg-sand/60 border border-border/50 rounded-2xl p-6 text-sm text-muted-foreground">
        <div className="font-medium text-forest mb-2">Upload tips</div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Supported: JPG, PNG, WebP, GIF, SVG · max 5 MB per image</li>
          <li>Use high-resolution images (min 1200px wide) for the hero and speciality slots</li>
          <li>Logo looks best as a PNG with a transparent background</li>
          <li>Uploaded images are saved to the clinic database — safe across redeploys</li>
        </ul>
      </div>
    </div>
  );
}
